import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { 
  Download as DownloadIcon, 
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { commitmentAPI, lookupAPI } from '../services/api';
import { Store, CommitmentOrderItem, CommitmentOrderUpdate } from '../types';

interface DailyCommitmentOrdersProps {
  selectedStore: Store | null;
}

const DailyCommitmentOrders: React.FC<DailyCommitmentOrdersProps> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [commitmentData, setCommitmentData] = useState<CommitmentOrderItem[]>([]);
  const [originalData, setOriginalData] = useState<CommitmentOrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(0);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      setSelectedStoreId(selectedStore.id.toString());
    }
  }, [selectedStore]);

  const loadStores = async () => {
    try {
      const storesData = await lookupAPI.getStores();
      setStores(storesData);
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('Failed to load stores');
    }
  };

  const loadCommitmentOrders = async () => {
    if (!selectedStoreId || !fromDate || !toDate) {
      setError('Please select store and date range');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await commitmentAPI.getOrders({
        store_id: selectedStoreId,
        date_from: fromDate.format('YYYY-MM-DD'),
        date_to: toDate.format('YYYY-MM-DD')
      });
      setCommitmentData(data.data);
      setOriginalData(data.data); // Store original data for reset functionality
    } catch (error) {
      console.error('Error loading commitment orders:', error);
      setError('Failed to load commitment orders');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuantity = (index: number) => {
    setEditingItem(index);
    setEditQuantity(commitmentData[index].suggested_quantity);
  };

  const handleSaveQuantity = (index: number) => {
    if (editQuantity < 0) {
      setError('Quantity cannot be negative');
      return;
    }

    // Update the local state only - no API call
    const updatedData = [...commitmentData];
    updatedData[index] = {
      ...updatedData[index],
      suggested_quantity: editQuantity
    };
    
    setCommitmentData(updatedData);
    setEditingItem(null);
    setEditQuantity(0);
    setError('');
  };

  const handleRemoveItem = (index: number) => {
    // Remove item from local state only - no API call
    const updatedData = commitmentData.filter((_, i) => i !== index);
    setCommitmentData(updatedData);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditQuantity(0);
  };

  const resetToOriginalData = () => {
    if (originalData.length > 0) {
      // If we have original data, restore it
      setCommitmentData([...originalData]);
    } else if (commitmentData.length > 0) {
      // If no original data but we have current data, reload from server
      loadCommitmentOrders();
    }
    setEditingItem(null);
    setEditQuantity(0);
    setError('');
  };

  const downloadCSV = () => {
    if (commitmentData.length === 0) {
      setError('No data to download');
      return;
    }

    const csvContent = [
      'manufacturer_name,medicine_name,pack_size,Suggested Quantity,Current Stock',
      ...commitmentData.map(item => 
        `"${item.manufacturer_name}","${item.medicine_name}","${item.pack_size || ''}",${item.suggested_quantity},${item.current_stock}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'commitment_order.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStockStatusColor = (currentStock: number) => {
    if (currentStock === 0) return 'error';
    if (currentStock < 10) return 'warning';
    return 'success';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Daily Commitment Orders
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={resetToOriginalData}
          disabled={loading}
        >
          {originalData.length > 0 ? 'Reset to Original' : 'Reload Data'}
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Orders
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Store</InputLabel>
                <Select
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                  label="Store"
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.store_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                onClick={loadCommitmentOrders}
                disabled={loading || !selectedStoreId || !fromDate || !toDate}
                fullWidth
              >
                {loading ? <CircularProgress size={20} /> : 'Load Orders'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {commitmentData.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Commitment Orders ({commitmentData.length} items)
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={downloadCSV}
                color="primary"
              >
                Download CSV
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Note: Changes made to quantities are temporary and will be included in the CSV download. Use "Reset to Original" to restore original values.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                                 <TableHead>
                   <TableRow>
                     <TableCell>Manufacturer</TableCell>
                     <TableCell>Medicine Name</TableCell>
                     <TableCell>Pack Size</TableCell>
                     <TableCell align="right">Suggested Quantity</TableCell>
                     <TableCell align="right">Current Stock</TableCell>
                     <TableCell align="center">Actions</TableCell>
                   </TableRow>
                 </TableHead>
                <TableBody>
                  {commitmentData.map((item, index) => (
                                         <TableRow key={index}>
                       <TableCell>{item.manufacturer_name}</TableCell>
                       <TableCell>{item.medicine_name}</TableCell>
                       <TableCell>{item.pack_size || '-'}</TableCell>
                       <TableCell align="right">
                         {editingItem === index ? (
                           <TextField
                             type="number"
                             value={editQuantity}
                             onChange={(e) => setEditQuantity(parseInt(e.target.value) || 0)}
                             size="small"
                             inputProps={{ min: 0 }}
                             sx={{ width: 80 }}
                           />
                         ) : (
                           item.suggested_quantity
                         )}
                       </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={item.current_stock}
                          color={getStockStatusColor(item.current_stock) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {editingItem === index ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSaveQuantity(index)}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={handleCancelEdit}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditQuantity(index)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {!loading && commitmentData.length === 0 && selectedStoreId && fromDate && toDate && (
        <Card>
          <CardContent>
            <Typography variant="body1" textAlign="center" color="text.secondary">
              No commitment orders found for the selected criteria.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DailyCommitmentOrders;
